/**
 * FocusChip — global header chip that surfaces the user's current focus
 * (WS-NAV-4).
 *
 * Why
 * ---
 * `ContextSidebar` already renders the same `useFocusContext` SSOT as a
 * right-rail at ≥1280px, but the rail is invisible at every narrower
 * width. The header chip is the second view over the same provider so a
 * mentor on a tablet or laptop-narrow viewport can still see who they
 * are working with and jump between recently-pinned entities. The two
 * views share a single SSOT (`useFocusContext`) — no parallel state.
 *
 * Composition
 * -----------
 * - Chip button (trigger): renders `focus.label`, opens a popover.
 *   Self-hides when `focus === null`, mirroring `ContextSidebar`'s
 *   null-guard so the same unconditional mount in `SurfaceHeader` is
 *   safe across every surface, mentorship or not.
 * - Popover (dialog): three primary actions on the current focus —
 *   Pin / Unpin / Unfocus — plus a "Recent pins" list (`pins.slice(-5)`,
 *   future-proof against a future MAX_PINS bump) where each row sets
 *   focus and an inline button unpins.
 *
 * Accessibility
 * -------------
 * - Trigger is a native `<button>` with `aria-expanded`, `aria-controls`.
 * - Popover has `role="dialog"` and is labelled by the focus name.
 * - Escape closes the popover and returns focus to the trigger.
 * - Outside-click closes the popover.
 *
 * Tokens
 * ------
 * Pure SSOT tokens (--space-*, --bg-*, --fg-*, --radius-*, --border-*).
 * No rgba literals, no magic px outside WCAG touch-target carve-outs.
 */

import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Pin, PinOff, X } from 'lucide-react';
import {
    MAX_PINS,
    useFocusContext,
    type FocusEntity,
} from '../../../modules/mentorship/focus/useFocusContext';
import { useKeyboardScope } from '../../keyboard';
import type { KeyComboShortcut } from '../../keyboard';
import { GLOSSARY } from '../../../modules/mentorship/glossary';
import './FocusChip.css';

/** How many pins to surface in the popover. Equal to `MAX_PINS` today,
 *  but using `slice(-RECENT_PINS_LIMIT)` future-proofs the chip if the
 *  cap ever rises: it always shows the most recent five. */
const RECENT_PINS_LIMIT = 5;

/** Render the user-facing label for a FocusEntity kind. Routes through
 *  `GLOSSARY` so mentorship concept terms (Submission / Meeting /
 *  Project) follow the SSOT label and stay in sync with every other
 *  surface. Mirrors `ContextSidebar.kindLabel` so the two focus views
 *  use identical copy. */
/** Mounts only while the popover is open. Owns the Escape-to-close
 *  shortcut via the keyboard SSOT so the dispatch composes with any
 *  surrounding modal stack. */
function FocusChipPopoverKeyScope({
    onEscape,
}: {
    onEscape: () => void;
}) {
    const shortcuts = useMemo<ReadonlyArray<KeyComboShortcut>>(
        () => [{ combo: 'escape', handler: () => onEscape() }],
        [onEscape],
    );
    useKeyboardScope(shortcuts, { owner: 'FocusChip' });
    return null;
}

function kindLabel(kind: FocusEntity['kind']): string {
    switch (kind) {
        case 'mentee':
            return 'Mentee';
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- focus-entity kind discriminator (infrastructure id), not user copy.
        case 'submission':
            return GLOSSARY.SUBMISSION.shortLabel;
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- focus-entity kind discriminator (infrastructure id), not user copy.
        case 'meeting':
            return GLOSSARY.MEETING.shortLabel;
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- focus-entity kind discriminator (infrastructure id), not user copy.
        case 'project':
            return GLOSSARY.PROJECT.shortLabel;
    }
}

function FocusChip() {
    const { focus, pins, pin, unpin, clearFocus, setFocus } = useFocusContext();
    const [open, setOpen] = useState(false);
    const popoverId = useId();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    // Outside-click closes the popover. Escape now routes through the
    // keyboard SSOT via `FocusChipPopoverKeyScope` (mounted below when
    // the popover is open), mirroring the SectionHelpButton controller
    // shape so all our popovers behave identically for keyboard users.
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (triggerRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open]);

    const handleEscape = useCallback(() => {
        setOpen(false);
        triggerRef.current?.focus();
    }, []);

    // No auto-close effect is needed when the focus drops away: the
    // chip self-hides (returns `null` below) the moment `focus` is
    // null, which unmounts the popover with it. Keeping derivation in
    // render avoids a cascading setState-in-effect path.

    const handleToggle = useCallback(() => {
        setOpen((v) => !v);
    }, []);

    const handleKeyDown = useCallback(
        (e: ReactKeyboardEvent<HTMLButtonElement>) => {
            // Space / Enter toggle. Native <button> handles this in
            // real browsers; jsdom doesn't always synthesise the click
            // for keydown-only flows so we handle it explicitly.
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setOpen((v) => !v);
            }
        },
        [],
    );

    // Self-hide when there is no focus. Keeps unconditional mounting in
    // `SurfaceHeader` safe across every surface, mentorship or not.
    if (!focus) return null;

    const focusIsPinned = pins.some((p) => p.id === focus.id);
    const canPin = !focusIsPinned && pins.length < MAX_PINS;
    const recentPins = pins.slice(-RECENT_PINS_LIMIT);

    const triggerLabel = `Current focus: ${kindLabel(focus.kind)} ${focus.label}`;

    return (
        <span className="focus-chip-wrap">
            <button
                ref={triggerRef}
                type="button"
                className="focus-chip"
                aria-label={triggerLabel}
                aria-expanded={open ? 'true' : 'false'}
                aria-controls={popoverId}
                aria-haspopup="dialog"
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
            >
                <span className="focus-chip__dot" aria-hidden="true" />
                <span className="focus-chip__label">{focus.label}</span>
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    id={popoverId}
                    role="dialog"
                    aria-label={triggerLabel}
                    className="focus-chip__popover"
                >
                    <FocusChipPopoverKeyScope onEscape={handleEscape} />
                    <header className="focus-chip__popover-head">
                        <div className="focus-chip__popover-title-group">
                            <p className="focus-chip__popover-kind">
                                {kindLabel(focus.kind)}
                            </p>
                            <p className="focus-chip__popover-label">
                                {focus.label}
                            </p>
                            {focus.sublabel && (
                                <p className="focus-chip__popover-sublabel">
                                    {focus.sublabel}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            className="focus-chip__close"
                            onClick={() => {
                                setOpen(false);
                                triggerRef.current?.focus();
                            }}
                            aria-label="Close focus menu"
                        >
                            <X size={14} aria-hidden="true" />
                        </button>
                    </header>

                    <div className="focus-chip__actions">
                        {focusIsPinned ? (
                            <button
                                type="button"
                                className="focus-chip__action"
                                onClick={() => unpin(focus.id)}
                            >
                                <PinOff size={14} aria-hidden="true" />
                                <span>Unpin</span>
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="focus-chip__action"
                                onClick={() => pin(focus)}
                                disabled={!canPin}
                                {...(!canPin
                                    ? { title: `Pin limit reached (${MAX_PINS})` }
                                    : {})}
                            >
                                <Pin size={14} aria-hidden="true" />
                                <span>Pin</span>
                            </button>
                        )}
                        <button
                            type="button"
                            className="focus-chip__action"
                            onClick={() => {
                                clearFocus();
                                setOpen(false);
                                triggerRef.current?.focus();
                            }}
                        >
                            <X size={14} aria-hidden="true" />
                            <span>Unfocus</span>
                        </button>
                    </div>

                    {recentPins.length > 0 && (
                        <div className="focus-chip__pins">
                            <p className="focus-chip__pins-title">Recent pins</p>
                            <ul className="focus-chip__pins-list">
                                {recentPins.map((p) => (
                                    <li
                                        key={p.id}
                                        className="focus-chip__pins-row"
                                    >
                                        <button
                                            type="button"
                                            className={
                                                'focus-chip__pins-select' +
                                                (focus.id === p.id
                                                    ? ' focus-chip__pins-select--active'
                                                    : '')
                                            }
                                            onClick={() => {
                                                setFocus(p);
                                                setOpen(false);
                                                triggerRef.current?.focus();
                                            }}
                                            aria-label={`Focus ${kindLabel(p.kind)} ${p.label}`}
                                        >
                                            <span className="focus-chip__pins-label">
                                                {p.label}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className="focus-chip__pins-remove"
                                            onClick={() => unpin(p.id)}
                                            aria-label={`Unpin ${p.label}`}
                                        >
                                            <PinOff size={12} aria-hidden="true" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </span>
    );
}

export default FocusChip;
